import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  createCommand,
  LexicalCommand,
  COMMAND_PRIORITY_NORMAL,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
} from 'lexical';
import { useEffect } from 'react';
import {
  $insertRowAbove,
  $insertRowBelow,
  $insertColumnLeft,
  $insertColumnRight,
  $deleteRow,
  $deleteColumn,
  $insertTable,
  $isSelectionInTable,
  $focusFirstCellInRow,
} from '../utils/tableUtils';

// Table manipulation commands
export const INSERT_TABLE_COMMAND: LexicalCommand<{
  rows?: number;
  columns?: number;
}> = createCommand();
export const INSERT_TABLE_ROW_ABOVE_COMMAND: LexicalCommand<void> =
  createCommand();
export const INSERT_TABLE_ROW_BELOW_COMMAND: LexicalCommand<void> =
  createCommand();
export const INSERT_TABLE_COLUMN_LEFT_COMMAND: LexicalCommand<void> =
  createCommand();
export const INSERT_TABLE_COLUMN_RIGHT_COMMAND: LexicalCommand<void> =
  createCommand();
export const DELETE_TABLE_ROW_COMMAND: LexicalCommand<void> = createCommand();
export const DELETE_TABLE_COLUMN_COMMAND: LexicalCommand<void> =
  createCommand();

export const TableManipulationPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Register command handlers
    const removeHandlers = [
      // Table creation command
      editor.registerCommand(
        INSERT_TABLE_COMMAND,
        ({ rows = 2, columns = 2 }) => {
          editor.update(() => {
            const success = $insertTable(rows, columns);
            if (!success) {
              console.warn('Failed to insert table - invalid selection');
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),

      // Row manipulation commands
      editor.registerCommand(
        INSERT_TABLE_ROW_ABOVE_COMMAND,
        () => {
          editor.update(() => {
            const success = $insertRowAbove();
            if (!success) {
              console.warn(
                'Failed to insert row above - cursor may not be in a table',
              );
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),

      editor.registerCommand(
        INSERT_TABLE_ROW_BELOW_COMMAND,
        () => {
          editor.update(() => {
            const newRow = $insertRowBelow();
            if (!newRow) {
              console.warn(
                'Failed to insert row below - cursor may not be in a table',
              );
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),

      editor.registerCommand(
        DELETE_TABLE_ROW_COMMAND,
        () => {
          editor.update(() => {
            const success = $deleteRow();
            if (!success) {
              console.warn(
                'Failed to delete row - cursor may not be in a table or table has only one row',
              );
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),

      // Column manipulation commands
      editor.registerCommand(
        INSERT_TABLE_COLUMN_LEFT_COMMAND,
        () => {
          editor.update(() => {
            const success = $insertColumnLeft();
            if (!success) {
              console.warn(
                'Failed to insert column left - cursor may not be in a table',
              );
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),

      editor.registerCommand(
        INSERT_TABLE_COLUMN_RIGHT_COMMAND,
        () => {
          editor.update(() => {
            const success = $insertColumnRight();
            if (!success) {
              console.warn(
                'Failed to insert column right - cursor may not be in a table',
              );
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),

      editor.registerCommand(
        DELETE_TABLE_COLUMN_COMMAND,
        () => {
          editor.update(() => {
            const success = $deleteColumn();
            if (!success) {
              console.warn(
                'Failed to delete column - cursor may not be in a table or table has only one column',
              );
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL,
      ),

      // Keyboard shortcut: Enter key adds row below when in table
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        event => {
          // Only handle if we're in a table and no modifier keys are pressed
          if (
            event &&
            (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey)
          ) {
            return false; // Let other handlers deal with modified Enter
          }

          let shouldAddRow = false;

          editor.getEditorState().read(() => {
            shouldAddRow = $isSelectionInTable();
          });

          if (shouldAddRow) {
            event?.preventDefault(); // Prevent default paragraph creation

            editor.update(() => {
              const newRow = $insertRowBelow();
              if (!newRow) {
                console.warn(
                  'Failed to insert row below - cursor may not be in a table',
                );
                return;
              }

              // Focus the first cell of the new row
              $focusFirstCellInRow(newRow);
            });

            return true; // Command handled
          }

          return false; // Let default behavior handle non-table cases
        },
        COMMAND_PRIORITY_HIGH, // High priority to intercept before default Enter handling
      ),
    ];

    // Return cleanup function
    return () => {
      removeHandlers.forEach(removeHandler => removeHandler());
    };
  }, [editor]);

  return null;
};
